import React, { useEffect, useState } from "react";
import {
  FiShoppingBag,
  FiDollarSign,
  FiHome,
  FiPlusCircle,
  FiSearch,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import api from "../../api/axios";
import { formatBdt } from "../../utils/leadershipRoles";
import "./MarketingOfficer.css";

const MarketingOfficer = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/api/leadership/marketing-officer");
        if (!cancelled) setData(res.data);
      } catch (err) {
        console.error("Marketing officer dashboard failed", err);
        if (!cancelled) {
          setError("Failed to load marketing overview");
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const history = data?.recent_history || [];
  const changePct = data?.commission_change_pct;
  const changeLabel =
    changePct == null
      ? "No prior month data"
      : `${changePct >= 0 ? "+" : ""}${changePct}% from last month`;

  return (
    <div className="mkt-officer-dashboard">
      <header className="mkt-officer-header">
        <div className="mkt-officer-welcome">
          <h1>Marketing Overview</h1>
          <p>Live sales and commission for your listings.</p>
        </div>
        <div className="mkt-officer-quick-actions-bar">
          <Link to="/dashboard/make-sale" className="mkt-officer-action-btn mkt-officer-primary">
            <FiPlusCircle /> <span>Make a Sale</span>
          </Link>
          <Link to="/properties" className="mkt-officer-action-btn mkt-officer-secondary">
            <FiSearch /> <span>Properties</span>
          </Link>
        </div>
      </header>

      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}

      <section className="mkt-officer-stats-grid">
        <div className="mkt-officer-stat-card mkt-officer-performance">
          <div className="mkt-officer-stat-icon">
            <FiShoppingBag />
          </div>
          <div className="mkt-officer-stat-data">
            <span className="mkt-officer-label">Units Sold (Monthly)</span>
            <h3>{loading ? "…" : `${data?.units_sold_month ?? 0} Units`}</h3>
            <div className="mkt-officer-progress-container">
              <div
                className="mkt-officer-progress-bar"
                style={{ width: `${data?.goal_pct ?? 0}%` }}
              ></div>
              <span>{data?.goal_pct ?? 0}% of Goal</span>
            </div>
          </div>
        </div>

        <div className="mkt-officer-stat-card mkt-officer-commission">
          <div className="mkt-officer-stat-icon">
            <FiDollarSign />
          </div>
          <div className="mkt-officer-stat-data">
            <span className="mkt-officer-label">Commission Generated</span>
            <h3>{loading ? "…" : formatBdt(data?.commission_month)}</h3>
            <span
              className={`mkt-officer-sub-text ${
                (changePct ?? 0) >= 0 ? "mkt-officer-positive" : "mkt-officer-negative"
              }`}
            >
              {changeLabel}
            </span>
          </div>
        </div>
      </section>

      <div className="mkt-officer-content-layout">
        <div className="mkt-officer-listings-container">
          <div className="mkt-officer-section-header">
            <h3>Portfolio Snapshot</h3>
          </div>
          <div className="mkt-officer-listings-grid">
            <div className="mkt-officer-listing-item-card">
              <FiHome className="mkt-officer-item-icon" />
              <h4>{loading ? "…" : data?.assigned_properties ?? 0}</h4>
              <p>Assigned Properties</p>
            </div>
          </div>
        </div>

        <div className="mkt-officer-wallet-history-container">
          <p className="text-muted small">
            Commission payouts and wallets are managed on Iconic Unity Group.
          </p>

          <div className="mkt-officer-payment-history">
            <h3>Commission History</h3>
            {history.length === 0 && !loading && (
              <p className="text-muted small mb-0">No commission history yet.</p>
            )}
            {history.map((item, idx) => (
              <div className="mkt-officer-payment-item" key={idx}>
                <div className="mkt-officer-p-date">{item.date}</div>
                <div className="mkt-officer-p-desc">{item.description}</div>
                <div className="mkt-officer-p-amt">
                  +{formatBdt(item.amount)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingOfficer;
